/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Homepage Component
 */

import { Box } from '@chakra-ui/react'
import { useMount } from '@zcode-ui/hook'
import { AnalyticsC } from '@zcode/nr-utils'
import { BLACK_LIGHTER } from '@zcode/nr/es/shared/constants/color'
import { goToHashIfInUrl } from '@zcode/nr/es/shared/utils'
import { trackNonInteractionEvent } from '@zcode/nr/es/shared/utils/analytics'
import React, { useEffect } from 'react'
import type { RouteComponentProps } from 'react-router'
import { getUrlParams } from '../../utils/urls/getUrlParams'
import { withAnalyticChannelProvider } from '../AnalyticChannelProvider/withAnalyticChannelProvider'
import { AppBannerContainer } from '../AppBanner/AppBannerContainer'
import { HeaderContainer } from '../HomepageHeader/HeaderContainer'
import { LandingPageSection } from '../LandingPageSection/LandingPageSection'
import { ManageTitle } from '../ManageTitle/ManageTitle'
import { FooterInnerContainer } from '../footer/FooterInnerContainer'
import { HomepageContent } from './HomepageContent'
import { HomepageLinksContainer } from './HomepageLinks/HomepageLinksContainer'
import { HOMEPAGE_MAIN_SEARCH } from './analytics'
import { TITLE } from './constants'
import { homepage } from './styles'

export interface HomepageProps {
  isLoggedIn: boolean
  isMessage3: boolean
  isMobile: boolean
  loadHashtagContent: (routeProps: Pick<RouteComponentProps, 'location'>) => void
  loadUnreadCount: () => void
  linkpacksLoaded: boolean
  loadLinkpacks: () => void
  onBlockedUserToken: (token: string | string[]) => void
  onMount: () => void
}

function getCustomProperties() {
  const computedStyle = getComputedStyle(document.documentElement)
  let customPropertyNames = Object.values(computedStyle).filter((propertyName) =>
    propertyName.startsWith('--')
  )
  if (!customPropertyNames.length) {
    // Chrome is broken, so it hits this line https://issues.chromium.org/issues/41451306
    // adapted from https://css-tricks.com/how-to-get-all-custom-properties-on-a-page-in-javascript/
    customPropertyNames = Array.prototype.filter
      .call<StyleSheetList, [(styleSheet: CSSStyleSheet) => boolean], Array<CSSStyleSheet>>(
        document.styleSheets,
        (styleSheet: CSSStyleSheet) =>
          !styleSheet.href || styleSheet.href.startsWith(document.location.origin)
      )
      .reduce<CSSRule[]>((rules, stylesheet) => rules.concat(...stylesheet.cssRules), [])
      .filter((rule) => rule instanceof CSSStyleRule)
      .reduce<string[]>(
        (propertyNames, rule) =>
          propertyNames.concat(
            Array.prototype.filter.call<
              CSSStyleDeclaration,
              [(propertyName: string) => boolean],
              Array<string>
            >(rule.style, (propertyName) => propertyName.startsWith('--'))
          ),
        []
      )
  }
  return customPropertyNames
    .map((key) => {
      const value = computedStyle.getPropertyValue(key)
      return value ? `${key}: ${value};` : ''
    })
    .join('')
}

const Homepage = ({
  isMobile,
  linkpacksLoaded,
  loadLinkpacks,
  onBlockedUserToken,
  onMount,
}: HomepageProps) => {
  const onSpecialUrlParams = () => {
    const { blocked_user_token: blockedUserToken } = getUrlParams()
    if (blockedUserToken) {
      onBlockedUserToken(blockedUserToken)
    }
  }

  useEffect(() => {
    // Sometimes djangoadmin wants to render some content using the
    // fonts and CSS variables that are available in web-zumper.
    // To facilitate this:
    //
    // 1. Admin loads the homepage in a hidden iframe
    // 2. Admin sends a message to the iframe ("tools:getStyles")
    // 3. This code here sends a message back with the styles
    //
    // see https://github.com/zumper/zumper_code/blob/master/svc/djangoadmin/django_admin/static/js/django-admin.js
    function onMessage(e: MessageEvent<unknown>) {
      if (
        e.source &&
        e.origin.startsWith('https://') &&
        e.origin.endsWith('.zumper.com') &&
        e.data === 'tools:getStyles'
      ) {
        e.source.postMessage(
          {
            styles: `${document.querySelector('link[href$="fonts.css"]')?.outerHTML ?? ''}<style>:root{${getCustomProperties()}}</style>`,
            type: 'tools:sendStyles',
          },
          { targetOrigin: e.origin }
        )
      }
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [])

  useMount(() => {
    trackNonInteractionEvent(AnalyticsC.HOMEPAGE, 'New NZR Homepage')
    onSpecialUrlParams()
    onMount?.()
    goToHashIfInUrl('landlords')
    if (!linkpacksLoaded) {
      loadLinkpacks()
    }
  })

  return (
    <Box<'div'> css={homepage}>
      {isMobile && <AppBannerContainer type="APP" />}
      <ManageTitle title={TITLE} />
      <HeaderContainer isZumperRentals={false} homepageMainSearch={HOMEPAGE_MAIN_SEARCH} />
      <HomepageContent />
      <HomepageLinksContainer />
      {/* Footers are shared components.
          As a result we want to wrap them with LandingPageSection in the Homepage for layout reasons. */}
      <LandingPageSection backgroundColor={BLACK_LIGHTER}>
        <FooterInnerContainer analyticChannel={AnalyticsC.HOMEPAGE} />
      </LandingPageSection>
    </Box>
  )
}

// eslint-disable-next-line import/no-default-export
export default withAnalyticChannelProvider({ analyticChannel: AnalyticsC.HOMEPAGE })(Homepage)
